import { keycloak } from '@/config';
import { SA_STATE } from '@/config/constants';
import { useState, useEffect } from 'react';
import axios from 'axios';
import * as identity from '@/clients/doorbell/identity';
import { TaskTableData } from '@/routes/Main/Projects/DeviceIdentity/partials/DeviceTaskList';
import moment from 'moment';
import { UserRegisterParams, KC_ROLE, KC_STATE } from '@/clients/keycloak/type';
import { SamsungUserDetails } from '@/clients/auth/type';
import UserRepresentation from '@keycloak/keycloak-admin-client/lib/defs/userRepresentation';
import { sbaas_mdr_api } from '@/config/sbaas';
import { kcInstance } from '@/routes/AuthLogin';
import { logOutUser } from '@/clients/auth/samsungAccount';

const sbaasUrl = process.env.REACT_APP_SBAAS_BASE || '';

export const crypt = (salt: string, text: string) => {
  const textToChars = (text: string) =>
    text.split('').map(c => c.charCodeAt(0));
  const byteHex = (n: any) => {
    const str1 = '0' + Number(n).toString(16);

    return str1.substring(str1.length - 2);
  };
  const applySaltToChar = (code: any) =>
    textToChars(salt).reduce((a, b) => a ^ b, code);

  return text
    .split('')
    .map(textToChars)
    .map(applySaltToChar)
    .map(byteHex)
    .join('');
};

export const decrypt = (salt: string, encoded: any) => {
  const textToChars = (text: string) =>
    text.split('').map(c => c.charCodeAt(0));
  const applySaltToChar = (code: any) =>
    textToChars(salt).reduce((a, b) => a ^ b, code);
  return encoded
    .match(/.{1,2}/g)
    .map((hex: any) => parseInt(hex, 16))
    .map(applySaltToChar)
    .map((charCode: any) => String.fromCharCode(charCode))
    .join('');
};

export const setStorage = (key: string, value: string) => {
  if (process.env.REACT_APP_ENCODE_STORAGE === 'true') {
    const keyCrypt = crypt(key, key);
    const valueCrypt = crypt(key, value);
    localStorage.setItem(keyCrypt, valueCrypt);
  } else localStorage.setItem(key, value);
};

export const getStorage = (key: string) => {
  if (process.env.REACT_APP_ENCODE_STORAGE === 'true') {
    const keyCrypt = crypt(key, key);
    const valueCrypt = localStorage.getItem(keyCrypt);
    return valueCrypt ? decrypt(key, valueCrypt) : null;
  } else return localStorage.getItem(key);
};

export const removeStorage = (key: string) => {
  if (process.env.REACT_APP_ENCODE_STORAGE === 'true') {
    const keyCrypt = crypt(key, key);
    localStorage.removeItem(keyCrypt);
  } else localStorage.removeItem(key);
};

export const removeStorageBulk = (keys: string[]) => {
  for (let key of keys) removeStorage(key);
};

export const generateSbaasKey = (userId: string, role: string) => {
  const now = new Date();
  const key = btoa(`${userId}::${role}::${now.valueOf()}`)
    .replace(/=+$/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
  return {
    key: key,
    date: now.valueOf(),
  };
};

export const generateActivationTokenUrl = (
  id: string,
  username: string,
  role: string,
) => {
  const urlParams = new URL(`${window.location.origin}`);
  urlParams.searchParams.append('activation', 'user');
  const text = `${id}::${username}::${role}::activation::user`;
  const key = btoa(crypt(SA_STATE, text));
  const text2 = `${key}::${SA_STATE}`;
  const key2 = btoa(text2);
  urlParams.searchParams.append('key', key2);
  return urlParams.href;
};

export const parseActivationToken = () => {
  const urlSearch = window.location.search;
  const params = new URLSearchParams(urlSearch);
  if (params.get('activation') === 'user' && params.get('key')) {
    try {
      const key = params.get('key') || '';
      const base = atob(key);
      const bArr = base.split('::');
      if (bArr.length === 2) {
        const base2 = atob(bArr[0]);
        const salt = bArr[1];
        const keyResult: string = decrypt(salt, base2);
        const keyArr = keyResult.split('::');
        if (keyArr.length === 5) {
          return {
            userId: keyArr[0],
            username: keyArr[1],
            role: keyArr[2],
            purpose: keyArr[3],
            target: keyArr[4],
          };
        }
      }
    } catch (err) {
      return undefined;
    }
  }
  return undefined;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const removeCommentFromKey = (key: string) => {
  let start = 0;
  let end = 0;
  let countTemp = 0;
  for (let i = 0; i < key.length; i++) {
    if (key.charAt(i) !== '-' && countTemp % 2 === 0) {
      countTemp++;
      if (countTemp === 3) start = i;
    } else if (key.charAt(i) === '-' && countTemp % 2 === 1) {
      countTemp++;
      if (countTemp === 4) end = i;
    }
  }
  return key.slice(start, end);
};

const activityArr = ['REGISTER', 'UPDATE_KEY', 'UPDATE_STATUS', 'DELETE'];

export type DeviceHistoryPayload = {
  userId: string;
  role?: string;
  projectId: string;
  mnId: number;
  sn: string;
  activity: typeof activityArr[number];
};
export const createDeviceHistory = (payload: DeviceHistoryPayload) => {
  sbaas_mdr_api.script('local_device_history').post({
    body: {
      ...generateSbaasKey(payload.userId, payload.role || 'Super Admin'),
      userId: payload.userId,
      device: payload,
    },
  });
};

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

export const uploadFileToS3 = async (file: File) => {
  const sbaasKey = generateSbaasKey('userId', 'SUPERADMIN');
  try {
    const kc: any = await kcInstance();
    const token = kc && kc.token;
    if (!token) {
      logOutUser();
      return Promise.reject('User Credential not found or expired');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', sbaasKey.key);
    formData.append('date', `${sbaasKey.date}`);
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: `Bearer ${keycloak.instance.token}`,
      },
    };
    const res = await axios.put(
      sbaasUrl + '/apps/st-mdr/script/upload_file_s3',
      formData,
      config,
    );
    return Promise.resolve<{ url: string; fileName: string }>(res.data);
  } catch (exception) {
    console.log(exception);
    return Promise.reject('Server Error');
  }
};

export const getFIleFromS3ByTaskId = async (taskId: string) => {
  const res = await sbaas_mdr_api.script('get_file_by_task_id').get({
    query: {
      ...generateSbaasKey('userId', 'SUPERADMIN'),
      taskId: taskId,
    },
  });
  const link = document.createElement('a');
  link.setAttribute('href', res.data.url);
  link.setAttribute('download', 'Device Task Download.csv');
  link.click();
};

export const executeTaskScheduler = () => {
  const apiCall = async (taskId: string, projectId: string) => {
    identity.getTaskDetails(taskId).then(async res => {
      const resData = res.data.data || '';
      const nowMin10 = moment().subtract(10, 'minute');
      if (typeof resData !== 'string') {
        if (resData.taskStatus === 'COMPLETED') {
          const task: Partial<TaskTableData> = {
            id: taskId,
            updated_at: moment().unix(),
            progress_status: 'COMPLETED',
            total_count: resData.totalCount,
            failed_count: resData.failedCount,
            completed_time: resData.completedTime,
            expiry_time: resData.expiryTime,
          };
          await sbaas_mdr_api.script('/task-update').put({
            body: {
              ...generateSbaasKey('userId', 'SUPERADMIN'),
              task: task,
            },
          });
          // await identity.updateProjectDeviceCount({
          //   mnId: resData.mnId,
          //   productId: resData.productId,
          //   projectId: projectId,
          // });
          const successTaskId: string[] = getStorage('SC_TK_ID')
            ? JSON.parse(getStorage('SC_TK_ID'))
            : [];
          successTaskId[successTaskId.length] = taskId;
          setStorage('SC_TK_ID', JSON.stringify(successTaskId));
        } else if (
          resData.taskStatus === 'CREATED' &&
          resData.createdTime < nowMin10.unix()
        ) {
          const successTaskId: string[] = getStorage('SC_TK_ID')
            ? JSON.parse(getStorage('SC_TK_ID'))
            : [];
          successTaskId[successTaskId.length] = taskId;
          setStorage('SC_TK_ID', JSON.stringify(successTaskId));
        } else {
          setTimeout(() => {
            apiCall(taskId, projectId);
          }, 1500);
        }
      } else {
        setTimeout(() => {
          apiCall(taskId, projectId);
        }, 1500);
      }
    });
  };

  if (getStorage('TK_ID')) {
    const processTaskId: string[] = getStorage('PC_TK_ID')
      ? JSON.parse(getStorage('PC_TK_ID'))
      : [];
    const taskIds: string[] = JSON.parse(getStorage('TK_ID'));
    const projectIds = JSON.parse(getStorage('TK_PRJ_ID'));
    for (let taskId of taskIds) {
      processTaskId[processTaskId.length] = taskId;
      apiCall(taskId, projectIds[taskId].projectId);
    }
    removeStorage('TK_ID');
    setStorage('PC_TK_ID', JSON.stringify(processTaskId));
  }
};

export const retrieveTaskId = (debug: boolean) => {
  const successTaskId: string[] = getStorage('SC_TK_ID')
    ? JSON.parse(getStorage('SC_TK_ID'))
    : [];
  const processTaskId: string[] = getStorage('PC_TK_ID')
    ? JSON.parse(getStorage('PC_TK_ID'))
    : [];
  const projectMap: object = getStorage('TK_PRJ_ID')
    ? JSON.parse(getStorage('TK_PRJ_ID'))
    : [];
  if (!debug) removeStorageBulk(['PC_TK_ID', 'SC_TK_ID', 'TK_PRJ_ID']);
  const taskIds: string[] = [];

  for (let taskId of processTaskId) {
    if (successTaskId.indexOf(taskId) < 0) taskIds[taskIds.length] = taskId;
    else delete projectMap[taskId];
  }

  if (!debug && taskIds.length > 0) {
    setStorage('TK_ID', JSON.stringify(taskIds));
    setStorage('TK_PRJ_ID', JSON.stringify(projectMap));
  }

  return {
    process: processTaskId,
    success: successTaskId,
    result: taskIds.length > 0,
  };
};

export const checkValidUUID = (str: string) => {
  // eslint-disable-next-line
  const regex = new RegExp(
    '^{?[A-F0-9a-f]{8}-[A-F0-9a-f]{4}-[A-F0-9a-f]{4}-[A-F0-9a-f]{4}-[A-F0-9a-f]{12}}?$',
  );
  return regex.test(str);
};

export const stringToHex = (str: string) => {
  let hex = '';
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    const hexValue = charCode.toString(16);

    // Pad with zeros to ensure two-digit representation
    hex += hexValue.padStart(2, '0');
  }
  return hex;
};

export const hexToString = (hex: string) => {
  let str = '';
  for (let i = 0; i < hex.length; i += 2) {
    const hexValue = hex.substring(i, i + 2);
    const decimalValue = parseInt(hexValue, 16);
    str += String.fromCharCode(decimalValue);
  }
  return str;
};

export const generateTextFile = (
  text: string,
  name: string,
  contentType: string,
) => {
  return new File([text], name, { type: contentType });
};

export const isHex = (str: string) => {
  const regexp = /^[0-9a-fA-F]+$/;
  return regexp.test(str);
};

export const logoutKeycloak = async () => {
  await keycloak.instance.logout({
    redirectUri: window.location.origin + '/login',
  });
};

export const generateKeycloakUserRep = (
  data: UserRegisterParams,
  userInit: SamsungUserDetails,
) => {
  const payload: UserRepresentation = {
    username: data.username,
    email:
      data.role === 'Manufacturer Admin' ? data.email : userInit.email || '',
    firstName: userInit.given_name || '',
    lastName: userInit.family_name || '',
    enabled: true,
    attributes: {
      preferred_role: KC_ROLE[data.role || 'Partner User'] || '',
      preferred_mnid: data.role === 'Manufacturer Admin' ? data.mnId : '',
      pic_email: data.role === 'Partner User' ? data.email : '',
      purpose_to_use: data.purpose,
      division: data.division,
      state: KC_STATE.PENDING,
    },
  };
  return payload;
};
